import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Table,
    Button,
    FormGroup,
    Label,
    Input,
    Pagination,
    PaginationItem,
    PaginationLink,
    Badge
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import {
    getAdminSessions,
    terminateAdminSession,
    getSecurityConfig,
    updateSecurityConfig
} from "api/ApiServices";
import Swal from "sweetalert2";

const Security = () => {
    // Session Management State
    const [activeSessions, setActiveSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalSessions, setTotalSessions] = useState(0);
    const [sessionsPerPage] = useState(10);

    // Security Config State
    const [configLoading, setConfigLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [aiSensitivity, setAiSensitivity] = useState(0.5);
    const [mandatory2FA, setMandatory2FA] = useState(false);
    const [e2eEncryption, setE2eEncryption] = useState(false);
    const [spamMaxLinks, setSpamMaxLinks] = useState(3);
    const [spamMaxMessages, setSpamMaxMessages] = useState(10);
    const [spamTimeWindowMs, setSpamTimeWindowMs] = useState(10000);
    const [spamRepeatThreshold, setSpamRepeatThreshold] = useState(5);

    const renderPaginationItems = (currentPage, totalItems, limit, onPageChange) => {
        const totalPagesCount = Math.ceil(totalItems / limit);
        if (totalPagesCount <= 1) return null;

        const pages = [];
        const delta = 2;

        pages.push(
            <PaginationItem key={1} active={currentPage === 1}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(1); }}>1</PaginationLink>
            </PaginationItem>
        );

        if (currentPage > delta + 2) {
            pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        const rangeStart = Math.max(2, currentPage - delta);
        const rangeEnd = Math.min(totalPagesCount - 1, currentPage + delta);

        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(
                <PaginationItem key={i} active={currentPage === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        if (currentPage < totalPagesCount - delta - 1) {
            pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        if (totalPagesCount > 1) {
            pages.push(
                <PaginationItem key={totalPagesCount} active={currentPage === totalPagesCount}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(totalPagesCount); }}>{totalPagesCount}</PaginationLink>
                </PaginationItem>
            );
        }

        return pages;
    };

    const fetchSessions = async (page = 1) => {
        setSessionsLoading(true);
        try {
            const response = await getAdminSessions(page, sessionsPerPage);
            if (response.status && response.payload) {
                setActiveSessions(response.payload.sessions || []);
                setTotalSessions(response.payload.total || 0);
            }
        } catch (error) {
            console.error("Error fetching sessions:", error);
        } finally {
            setSessionsLoading(false);
        }
    };

    const fetchConfig = async () => {
        setConfigLoading(true);
        try {
            const response = await getSecurityConfig();
            if (response.status && response.payload) {
                const config = response.payload;
                setAiSensitivity(Math.max(0.2, Math.min(1.0, config.ai_sensitivity !== undefined ? config.ai_sensitivity : 0.5)));
                setMandatory2FA(config.mandatory_2fa === "1");
                setE2eEncryption(config.e2e_encryption === "1");
                setSpamMaxLinks(config.spam_max_links !== undefined ? config.spam_max_links : 3);
                setSpamMaxMessages(config.spam_max_messages !== undefined ? config.spam_max_messages : 10);
                setSpamTimeWindowMs(config.spam_time_window_ms !== undefined ? config.spam_time_window_ms : 10000);
                setSpamRepeatThreshold(config.spam_repeat_threshold !== undefined ? config.spam_repeat_threshold : 5);
            }
        } catch (error) {
            console.error("Error fetching security config:", error);
        } finally {
            setConfigLoading(false);
        }
    };

    useEffect(() => {
        fetchSessions(currentPage);
        fetchConfig();
    }, [currentPage]);

    const handleSaveConfig = async () => {
        setSaving(true);
        try {
            const response = await updateSecurityConfig({
                ai_sensitivity: aiSensitivity,
                mandatory_2fa: mandatory2FA ? "1" : "0",
                e2e_encryption: "1",
                spam_max_links: Number(spamMaxLinks),
                spam_max_messages: Number(spamMaxMessages),
                spam_time_window_ms: Number(spamTimeWindowMs),
                spam_repeat_threshold: Number(spamRepeatThreshold)
            });

            if (response.status) {
                Swal.fire({
                    title: "Success",
                    text: "Security configuration updated successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                Swal.fire("Error", response.message || "Failed to update configuration", "error");
            }
        } catch (error) {
            Swal.fire("Error", "An unexpected error occurred while saving", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleTerminate = async (sessionId) => {
        const result = await Swal.fire({
            title: 'Terminate Session?',
            text: "This will log out the device immediately.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#f5365c',
            confirmButtonText: 'Yes, terminate it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await terminateAdminSession(sessionId);
                if (response.status) {
                    Swal.fire('Terminated!', 'Session has been terminated.', 'success');
                    fetchSessions(currentPage);
                } else {
                    Swal.fire('Error', response.message || 'Failed to terminate session', 'error');
                }
            } catch (error) {
                Swal.fire('Error', 'An unexpected error occurred', 'error');
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString();
    };

    const totalPages = Math.ceil(totalSessions / sessionsPerPage);

    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid>
                <Row>
                    <Col lg="6">
                        <Card className="shadow">
                            <CardHeader className="border-0">
                                <h3 className="mb-0">
                                    <i className="ni ni-lock-circle-open text-danger mr-2" /> Security & AI Moderation Config
                                </h3>
                            </CardHeader>
                            <CardBody>
                                {configLoading ? (
                                    <div className="text-center py-5">
                                        <i className="fas fa-spinner fa-spin fa-2x text-primary" />
                                    </div>
                                ) : (
                                    <>
                                        <FormGroup>
                                            <Label>AI Moderation Sensitivity (Hate Speech)</Label>
                                            <Input
                                                type="range"
                                                className="form-control-alternative custom-range"
                                                min="0.2"
                                                max="1.0"
                                                step="0.01"
                                                value={aiSensitivity}
                                                onChange={(e) => setAiSensitivity(parseFloat(e.target.value))}
                                            />
                                            <div className="d-flex justify-content-between mt-2">
                                                <small className="text-muted">
                                                    Threshold: <span className="text-primary font-weight-bold">{aiSensitivity.toFixed(2)}</span>
                                                    {" — "}
                                                    {aiSensitivity >= 0.7 ? (
                                                        <span className="text-danger font-weight-bold">Threshold High (0.7 - 1.0): Flags only strong hate speech or severe violations.</span>
                                                    ) : aiSensitivity >= 0.4 ? (
                                                        <span className="text-warning font-weight-bold">Threshold Medium (0.4 - 0.7): Mostly flags harassment and hate speech.</span>
                                                    ) : (
                                                        <span className="text-info font-weight-bold">Threshold Low (0.2 - 0.4): May flag toxic or insulting messages.</span>
                                                    )}
                                                </small>
                                            </div>
                                        </FormGroup>
                                        <FormGroup check className="mb-3">
                                            <Label check>
                                                <Input
                                                    type="checkbox"
                                                    checked={mandatory2FA}
                                                    onChange={(e) => setMandatory2FA(e.target.checked)}
                                                /> Enable Mandatory 2FA for Admins
                                            </Label>
                                        </FormGroup>
                                         <div className="mb-3 d-flex align-items-center">
                                             <span className="text-dark font-weight-bold mr-2">End-to-End Encryption for DM Rooms:</span>
                                             <Badge color="success">Enabled</Badge>
                                         </div>
                                        <Button
                                            color="primary"
                                            onClick={handleSaveConfig}
                                            disabled={saving}
                                        >
                                            {saving ? <i className="fas fa-spinner fa-spin mr-2" /> : "Save Configuration"}
                                        </Button>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg="12 mt-3">
                        <Card className="shadow">
                            <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">
                                    <i className="ni ni-lock-circle-open text-danger mr-2" /> Active Sessions Management
                                </h3>
                                <Button color="primary" size="sm" onClick={() => fetchSessions(currentPage)}>
                                    <i className="fas fa-sync" />
                                </Button>
                            </CardHeader>
                            <Table className="align-items-center table-flush" responsive>
                                <thead className="thead-light">
                                    <tr>
                                        <th scope="col">User</th>
                                        <th scope="col">Device / OS</th>
                                        <th scope="col">IP Address</th>
                                        <th scope="col">Location</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Last Active / Terminated</th>
                                        <th scope="col">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sessionsLoading ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">
                                                <i className="fas fa-spinner fa-spin text-primary" />
                                            </td>
                                        </tr>
                                    ) : activeSessions.length > 0 ? (
                                        activeSessions.map((session) => (
                                            <tr key={session.as_uuid}>
                                                <th scope="row">
                                                    <div className="d-flex flex-column">
                                                        <span>{session.as_admin_name || "Admin"}</span>
                                                        <small className="text-muted">{session.as_admin_email || "N/A"}</small>
                                                    </div>
                                                </th>
                                                <td style={{ maxWidth: "250px", wordBreak: "break-word", whiteSpace: "normal" }}>
                                                    <span className="font-weight-bold">{session.as_device}</span>
                                                </td>
                                                <td>{session.as_ip}</td>
                                                <td>{session.as_location || "Unknown"}</td>
                                                <td>
                                                    <Badge
                                                        color={
                                                            session.as_status === "active" ? "success" :
                                                                session.as_status === "terminated" ? "danger" : "warning"
                                                        }
                                                        pill
                                                    >
                                                        {session.as_status}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {session.as_status === "active" ? (
                                                        formatDate(session.as_last_active)
                                                    ) : (
                                                        <div className="d-flex flex-column">
                                                            <small className="text-muted">Ended at:</small>
                                                            <span>{formatDate(session.as_terminated_at)}</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    {session.as_status === "active" && (
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => handleTerminate(session.as_uuid)}
                                                        >
                                                            Terminate
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4 text-muted">
                                                No other active sessions found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                            <CardFooter className="py-4 border-top bg-transparent d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                    Showing {((currentPage - 1) * sessionsPerPage) + 1} to {Math.min(currentPage * sessionsPerPage, totalSessions)} of {totalSessions} total sessions
                                </small>
                                {totalPages > 1 && (
                                    <nav aria-label="...">
                                        <Pagination
                                            className="pagination justify-content-end mb-0"
                                            listClassName="justify-content-end mb-0"
                                        >
                                            <PaginationItem disabled={currentPage === 1 || sessionsLoading}>
                                                <PaginationLink
                                                    href="#pablo"
                                                    onClick={(e) => { e.preventDefault(); setCurrentPage(currentPage - 1); }}
                                                    tabIndex="-1"
                                                >
                                                    <i className="fas fa-angle-left" />
                                                </PaginationLink>
                                            </PaginationItem>

                                            {renderPaginationItems(currentPage, totalSessions, sessionsPerPage, setCurrentPage)}

                                            <PaginationItem disabled={currentPage === totalPages || totalSessions === 0 || sessionsLoading}>
                                                <PaginationLink
                                                    href="#pablo"
                                                    onClick={(e) => { e.preventDefault(); setCurrentPage(currentPage + 1); }}
                                                >
                                                    <i className="fas fa-angle-right" />
                                                </PaginationLink>
                                            </PaginationItem>
                                        </Pagination>
                                    </nav>
                                )}
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default Security;
